import Head from 'next/head';
import React, { useEffect, JSX, use } from 'react';
// import { getRecaptchaKey } from 'lib/util';
import {
    Placeholder,
    VisitorIdentification,
    useSitecoreContext,
    Field,
    ImageField,
    Item,
    ComponentRendering,
    HtmlElementRendering,
    RouteData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import InnovationHeader from 'lib/InnovationHeader';
import GetCareHeader from 'lib/GetCareHeader';

import { LayoutServiceData, HTMLLink } from '@sitecore-jss/sitecore-jss-nextjs';
import ThemeContext from 'components/context/ThemeContext';
import 'aos/dist/aos.css';
import AOS from 'aos';
import { updateAllAriaLabels } from 'assets/utils/ariaLabelUtils';
import GetBreakpoint from 'assets/utils/getBreakPoint';
import Constants from 'assets/utils/constant';
import { readSchemaField, toJsonLdScript } from 'lib/jsonLd';

export interface LayoutProps {
    layoutData: LayoutServiceData;
    headLinks: HTMLLink[];
    context: ExtendedStyleguideSitecoreContextValue;
    schemaText?: unknown;
}
//const recaptchaKey = getRecaptchaKey();

// This is boilerplate navigation for sample purposes. Most apps should throw this away and use their own navigation implementation.
// Most apps may also wish to use GraphQL for their navigation construction; this sample does not simply to support disconnected mode.

// Add these type definitions at the top of the file
interface RouteFields {
    'Page Title': Field<string>;
    MetaDescription: Field<string>;
    'Meta Keyword': Field<string>;
    TwitterCardType: Field<string>;
    OpenGraphSiteName: Field<string>;
    OpenGraphTitle: Field<string>;
    NoIndex: Field<boolean>;
    NoFollow: Field<boolean>;
    OpenGraphDescription: Field<string>;
    OpenGraphImageUrl: ImageField;
    OpenGraphType: Field<string>;
    [key: string]: Field<string> | Field<boolean> | ImageField | Item | Item[];
}

type PlaceholdersData = {
    [key: string]: (ComponentRendering | HtmlElementRendering)[];
};

// Update the route type in StyleguideSitecoreContextValue
export type ExtendedStyleguideSitecoreContextValue = {
    sitecore: {
        context: {
            pageEditing?: boolean;
            site?: {
                name: string;
            };
            itemPath?: string;
        };
        route: RouteData & {
            name: string;
            fields: RouteFields;
            placeholders: PlaceholdersData;
        };
    };
    site?: {
        name: string;
        [key: string]: unknown;
    };
    itemPath?: string;
    itemId?: string;
};

new InnovationHeader(true).init();
new GetCareHeader(true).init();

function hideClareOnMobile() {
    const widget = document.getElementById('widgetRoot');
    if (!widget) return;

    const currentPath = window.location.pathname.toLocaleLowerCase();
    const isHidePage = Constants.clareWidget.hideOnMobilePaths.some((path) =>
        currentPath.startsWith(path.toLocaleLowerCase())
    );

    const breakpoint = GetBreakpoint();
    const isMobile = breakpoint === 'xs' || breakpoint === 'sm';

    widget.style.display = isHidePage && isMobile ? 'none' : '';
}


const Layout = ({ context, schemaText }: LayoutProps): JSX.Element => {
    const { updateSitecoreContext } = useSitecoreContext({ updatable: true });
    const themeState = use(ThemeContext);
    const setThemeState = themeState?.setThemeState;

    useEffect(() => {
        if (
            context.sitecore.context.itemPath
                ?.toLocaleLowerCase()
                .includes('saint-anthony-college-of-nursing') ||
            context.sitecore.context.itemPath
                ?.toLocaleLowerCase()
                .includes('saint-francis-medical-center-college-of-nursing')
        ) {
            // Wait for hydration to finish
            setTimeout(() => {
                AOS.init({ duration: 600, once: true });
                AOS.refreshHard();
            }, 0);
        }
    }, []);

    const site = context.site?.name?.toLowerCase() ?? '';
    const path = context.sitecore.context.itemPath?.toLowerCase() ?? '';

    const RULES = [
    { test: () => path.includes('saint-anthony-college-of-nursing'),
        theme: () => path.includes('faculty-staff') ? 'sacon faculty-staff' : 'sacon' },
    { test: () => path.includes('saint-francis-medical-center-college-of-nursing'),
        theme: () => path.includes('faculty-staff') ? 'sfcon faculty-staff' : 'sfcon' },
    { test: () => path.includes('/hospitals/childrens') || path.includes('c/childrens'),
        theme: () => 'choi' },
    { test: () => path.includes('oncall'),   theme: () => 'oncall' },
    { test: () => path.includes('sisters-history'), theme: () => 'sisters' },
    { test: () => site.includes('osfhealthcare'),
        theme: () => path.includes('/c/') ? 'osfhealthcare landing-page' : 'osfhealthcare' },
    ];

    const theme = RULES.find(r => r.test())?.theme() ?? 'osfhealthcare';

    useEffect(() => {
        updateSitecoreContext && updateSitecoreContext(context);
        document?.body.setAttribute('data-theme', theme);
        setThemeState?.({ theme });
    }, [context]);

    const route = context.sitecore.route;

    const pageTitle =
        route.fields &&
            'Page Title' in route.fields &&
            route.fields['Page Title']?.value &&
            route.fields['Page Title'].value !== '$name'
            ? `${route.fields['Page Title'].value} | OSF HealthCare`
            : `${route.name[0].toUpperCase() + route.name.slice(1)} | OSF HealthCare`;

    const schemaJsonLd =
        toJsonLdScript(schemaText) ??
        toJsonLdScript(readSchemaField(route.fields as Record<string, unknown>));

    const relativePathStartsWith = (path: unknown, value: string): boolean => {
        return typeof path === 'string' && path.startsWith(`${value}`);
    };

    useEffect(() => {
        hideClareOnMobile();

        const observer = new MutationObserver(() => hideClareOnMobile());
        observer.observe(document.body, { childList: true, subtree: true });

        window.addEventListener('resize', hideClareOnMobile);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', hideClareOnMobile);
        };
    }, [context]);

    useEffect(() => {
        const id = window.requestIdleCallback
            ? window.requestIdleCallback(updateAllAriaLabels)
            : window.setTimeout(updateAllAriaLabels, 0);
        return () => {
            if (window.cancelIdleCallback) window.cancelIdleCallback(id);
            else clearTimeout(id);
        };
    }, [context]);

    useEffect(() => {
        const scrollToHash = () => {
            const hash = window.location.hash;
            if (!hash) return;

            const el = document.getElementById(hash.substring(1));
            if (!el) return;

            const rect = el.getBoundingClientRect();

            // -150 is the height of the header roughly estimated.
            const scrollTop = rect.top + window.scrollY - 150;

            window.scroll({
                top: scrollTop,
                behavior: 'smooth',
            });
        };
        scrollToHash();

        window.addEventListener('load', scrollToHash);
        return () => {
            window.removeEventListener('load', scrollToHash);
        };
    }, []);

    return (
        <>
            <Head>
                {schemaJsonLd && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: schemaJsonLd }}
                        key="sitecore-jsonld"
                    />
                )}
                {/* OUR Recaptcha URL */}
                {/* <script
					src={`https://www.google.com/recaptcha/enterprise.js?render=${recaptchaKey}`}
				></script> */}
                <script
                    dangerouslySetInnerHTML={{
                        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                        })(window,document,'script','dataLayer','GTM-KHGKWQN');`,
                    }}
                ></script>
                <link
                    href="https://fonts.googleapis.com/css?family=Lato:400,700"
                    rel="stylesheet"
                ></link>
                {(() => {
                    const noindex = (route.fields['NoIndex'])?.value;
                    const nofollow = (route.fields['NoFollow'])?.value;

                    if (!noindex && !nofollow) return null;

                    const content = [
                        noindex ? 'noindex' : '',
                        nofollow ? 'nofollow' : '',
                    ]
                        .filter(Boolean)
                        .join(', ');

                    return <meta name="robots" content={content} />;
                })()}
                <meta
                    name="google-site-verification"
                    content="5uojjV9CUMlSlDis96XduGZM6U1uR7z14kj0e2Cb5ks"
                />
                {route.fields['Page Title'].value !== '*' && <title>{pageTitle}</title>}
                <link rel="icon" href="/favicon.png" />

                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, minimum-scale=1"
                />

                {route.fields['Page Title'].value !== '*' && (
                    <meta
                        name="description"
                        content={
                            route.fields &&
                            (route.fields.MetaDescription?.value !== '*'
                                ? route.fields.MetaDescription?.value
                                : '')
                        }
                    />
                )}
                <meta
                    name="keywords"
                    content={
                        route.fields &&
                        (route.fields['Meta Keyword']?.value !== '$name'
                            ? route.fields['Meta Keyword']?.value
                            : '')
                    }
                />

                {route.fields.TwitterCardType?.value !== '*' && (
                    <meta
                        name="twitter:card"
                        content={route.fields && route.fields.TwitterCardType?.value}
                    />
                )}
                {route.fields.OpenGraphSiteName?.value !== '*' && (
                    <meta
                        name="twitter:site"
                        content={route.fields && route.fields.OpenGraphSiteName?.value}
                    />
                )}
                {route.fields.OpenGraphTitle?.value !== '*' && (
                    <meta
                        name="twitter:title"
                        content={route.fields && route.fields.OpenGraphTitle?.value}
                    />
                )}
                {route.fields.OpenGraphDescription?.value !== '*' && (
                    <meta
                        name="twitter:description"
                        content={route.fields && route.fields.OpenGraphDescription?.value}
                    />
                )}
                {route.fields.OpenGraphImageUrl?.value?.src && (
                    <meta
                        name="twitter:image"
                        content={route.fields.OpenGraphImageUrl.value.src}
                    />
                )}
                {route.fields.OpenGraphType?.value !== '*' && (
                    <meta
                        property="og:type"
                        content={route.fields && route.fields.OpenGraphType?.value}
                    />
                )}
                {route.fields.OpenGraphTitle?.value !== '*' && (
                    <meta
                        property="og:title"
                        content={route.fields && route.fields.OpenGraphTitle?.value}
                    />
                )}
                {route.fields.OpenGraphDescription?.value !== '*' && (
                    <meta
                        property="og:description"
                        content={route.fields && route.fields.OpenGraphDescription?.value}
                    />
                )}
                {route.fields.OpenGraphSiteName?.value !== '*' && (
                    <meta
                        property="og:url"
                        content={route.fields && route.fields.OpenGraphSiteName?.value}
                    />
                )}
                {route.fields.OpenGraphImageUrl?.value?.src && (
                    <meta
                        property="og:image"
                        content={route.fields.OpenGraphImageUrl.value.src}
                    />
                )}
            </Head>

            {/*
        VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
        If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
        For XM (CMS-only) apps, this should be removed.

        VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
      */}
            <VisitorIdentification />

            <noscript>
                <iframe
                    src="https://www.googletagmanager.com/ns.html?id=GTM-KHGKWQN"
                    height="0"
                    width="0"
                    style={{ display: 'none', visibility: 'hidden' }}
                ></iframe>
            </noscript>

            {/* root placeholder for the app, which we add components to using route data */}
            {(() => {
                if (
                    relativePathStartsWith(context.sitecore.context.itemPath, '/care')
                ) {
                    return (
                        <div className="jss-container">
                            <Placeholder name="r2-header" rendering={route} />
                            <div className="get-care-container">
                                <div className="content-container">
                                    <Placeholder name="gcn-page-title" rendering={route} />
                                    <div className="row">
                                        <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                                            <Placeholder name="gcn-left-column" rendering={route} />
                                        </div>
                                        <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                                            <Placeholder name="gcn-right-column" rendering={route} />
                                        </div>
                                        <div className="col col-12">
                                            <Placeholder name="gcn-full-width" rendering={route} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Placeholder name="r2-footer" rendering={route} />
                        </div>
                    );
                } else if (
                    relativePathStartsWith(context.sitecore.context.itemPath, '/gic')
                ) {
                    return (
                        <div className="jss-container">
                            <Placeholder name="jss-header" rendering={route} />
                            <Placeholder name="jss-main" rendering={route} />
                            <Placeholder name="jss-footer" rendering={route} />
                        </div>
                    );
                } else {
                    return (
                        <div className="jss-container">
                            <div id="r2-header-plac">
                                <Placeholder name="r2-header" rendering={route} />
                            </div>
                            <div id="r2-main-plac">
                                <Placeholder name="r2-main" rendering={route} />
                            </div>
                            <div id="r2-footer-plac">
                                <Placeholder name="r2-footer" rendering={route} />
                            </div>
                        </div>
                    );
                }
            })()}
        </>
    );
};

export default Layout;
